/**
 * CFS / GFS forecast menu registry.
 * Each leaf menu key is `{model}.{var}` (e.g. cfs.sst, gfs.wind).
 * Legacy 7.1.5 / 7.1.6 map to SST for backwards compatibility.
 */

export const FORECAST_ALIASES = {
  '7.1.5': { model: 'cfs', varKey: 'sst' },
  '7.1.6': { model: 'gfs', varKey: 'sst' },
};

/** @type {Record<string, { model: 'cfs'|'gfs', varKey: string, label: string, menuGroup: string }>} */
export const FORECAST_ENTRIES = {
  // Ocean — temperature
  'cfs.sst': {
    model: 'cfs',
    varKey: 'sst',
    label: 'CFS Ocean Temperature',
    menuGroup: 'ocean-temperature',
  },
  'gfs.sst': {
    model: 'gfs',
    varKey: 'sst',
    label: 'GFS Surface Temperature',
    menuGroup: 'ocean-temperature',
  },
  // Ocean — sea level / height
  'cfs.ssh': {
    model: 'cfs',
    varKey: 'ssh',
    label: 'CFS Ocean Height',
    menuGroup: 'sea-level',
  },
  // Ocean — salinity
  'cfs.sss': {
    model: 'cfs',
    varKey: 'sss',
    label: 'CFS Ocean Salinity',
    menuGroup: 'salinity',
  },
  // Ocean — currents
  'cfs.current': {
    model: 'cfs',
    varKey: 'current',
    label: 'CFS Ocean Current',
    menuGroup: 'currents',
  },
  // Atmosphere — air temperature
  'cfs.airtmp': {
    model: 'cfs',
    varKey: 'airtmp',
    label: 'CFS Air Temperature',
    menuGroup: 'air-temperature',
  },
  'gfs.airtmp': {
    model: 'gfs',
    varKey: 'airtmp',
    label: 'GFS Air Temperature',
    menuGroup: 'air-temperature',
  },
  // Atmosphere — rainfall
  'cfs.precip': {
    model: 'cfs',
    varKey: 'precip',
    label: 'CFS Precipitation',
    menuGroup: 'rainfall',
  },
  'gfs.precip': {
    model: 'gfs',
    varKey: 'precip',
    label: 'GFS Precipitation',
    menuGroup: 'rainfall',
  },
  // Atmosphere — wind
  'cfs.wind': {
    model: 'cfs',
    varKey: 'wind',
    label: 'CFS Wind',
    menuGroup: 'wind',
  },
  'gfs.wind': {
    model: 'gfs',
    varKey: 'wind',
    label: 'GFS Wind',
    menuGroup: 'wind',
  },
  // Atmosphere — pressure
  'cfs.pressure': {
    model: 'cfs',
    varKey: 'pressure',
    label: 'CFS Pressure',
    menuGroup: 'pressure',
  },
  'gfs.pressure': {
    model: 'gfs',
    varKey: 'pressure',
    label: 'GFS Pressure',
    menuGroup: 'pressure',
  },
  // Atmosphere — humidity
  'cfs.spfh': {
    model: 'cfs',
    varKey: 'spfh',
    label: 'CFS Specific Humidity',
    menuGroup: 'humidity',
  },
  'gfs.spfh': {
    model: 'gfs',
    varKey: 'spfh',
    label: 'GFS Specific Humidity',
    menuGroup: 'humidity',
  },
  // Ekman upwelling (tuna migration tool — Indo-Pacific only)
  'ekman.metop': {
    model: 'metop',
    varKey: 'ekman',
    label: 'MetOp Ekman Upwelling (Historical)',
    menuGroup: 'ekman',
  },
  'ekman.cfs': {
    model: 'cfs',
    varKey: 'ekman',
    label: 'CFS Ekman Upwelling (Forecast)',
    menuGroup: 'ekman',
  },
  'ekman.gfs': {
    model: 'gfs',
    varKey: 'ekman',
    label: 'GFS Ekman Upwelling (Forecast)',
    menuGroup: 'ekman',
  },
};

export function parseForecastId(variable) {
  if (!variable || typeof variable !== 'string') return null;
  if (FORECAST_ALIASES[variable]) return { ...FORECAST_ALIASES[variable], id: variable };
  const entry = FORECAST_ENTRIES[variable];
  if (!entry) return null;
  return { model: entry.model, varKey: entry.varKey, id: variable, label: entry.label };
}

export function isForecastVariable(variable) {
  return Boolean(parseForecastId(variable));
}

/** Returns true for the three Ekman tuna-migration menu keys. */
export function isEkmanVariable(variable) {
  return typeof variable === 'string' && variable.startsWith('ekman.');
}

export function forecastPageTitle(variable) {
  const parsed = parseForecastId(variable);
  if (!parsed) return null;
  const entry = FORECAST_ENTRIES[`${parsed.model}.${parsed.varKey}`];
  return entry?.label || `${parsed.model.toUpperCase()} Forecast`;
}
